/**
 * Workflow Migration Audit Utility
 * 
 * Identifies components still using raw stage checks,
 * invalid direct status writers, and workflow paths
 * bypassing engine logic.
 * 
 * Admin/dev-only — does not affect end users.
 */

import { STATUS_OWNERSHIP, type StatusFieldKey } from "./workflowEngine";

export interface WorkflowViolation {
  file: string;
  type: "raw_stage_check" | "direct_status_write" | "engine_bypass" | "inline_derivation";
  description: string;
  severity: "info" | "warning" | "critical";
}

/**
 * Static audit results — files known to still use raw stage checks
 * instead of centralized workflow helpers. Updated during migration passes.
 */
export function getRawStageCheckFiles(): WorkflowViolation[] {
  return [
    // Already migrated (using engine)
    { file: "src/pages/OrderingQueue.tsx", type: "raw_stage_check", description: "Migrated to canEnterStage()", severity: "info" },
    { file: "src/pages/InstallReadiness.tsx", type: "raw_stage_check", description: "Migrated to canEnterStage()", severity: "info" },
    { file: "src/data/nextStepEngine.ts", type: "raw_stage_check", description: "Migrated to getStageConfig/getNextStages", severity: "info" },
    { file: "src/data/suggestedActions.ts", type: "raw_stage_check", description: "Migrated to getStageConfig/getNextStages", severity: "info" },
    // Migrated in this pass
    { file: "src/components/dashboards/PipelineView.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/pages/SchedulingCalendar.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/hooks/useWorkflowOrchestration.ts", type: "raw_stage_check", description: "Status ownership enforcement added", severity: "info" },
    { file: "src/pages/JobDetail.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig/getOrderedStages", severity: "info" },
    { file: "src/data/customerNotifications.ts", type: "raw_stage_check", description: "Migrated to getStageConfig for milestone progression", severity: "info" },
    { file: "src/components/dashboards/OwnerDashboard.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/components/dashboards/OpsManagerDashboard.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/components/dashboards/AdminDashboard.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    // Migrated in pass 3
    { file: "src/pages/Reports.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig/getOrderedStages", severity: "info" },
    { file: "src/components/dashboards/SalesDashboard.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/components/dashboards/InstallerDashboard.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/components/dashboards/WarehouseCommandCenter.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/data/alertsEngine.ts", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/data/unifiedMetrics.ts", type: "raw_stage_check", description: "Migrated to isJobCompleted/isJobOpen helpers", severity: "info" },
    { file: "src/components/MaterialPlanningPanel.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/pages/InstallerDashboardPage.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/pages/ProjectCloseout.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/components/BuildReviewPanel.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
    { file: "src/components/JobWorkflowStatus.tsx", type: "raw_stage_check", description: "Migrated to getStageConfig()", severity: "info" },
  ];
}

/**
 * Files that directly write status fields without going through
 * the workflow engine's ownership validation.
 */
export function getDirectStatusWriters(): WorkflowViolation[] {
  return [
    { file: "src/components/dashboards/PipelineView.tsx", type: "direct_status_write", description: "Writes materialReadiness, planningStatus, currentStage via setJobs — validated by engine", severity: "info" },
    { file: "src/pages/SchedulingCalendar.tsx", type: "direct_status_write", description: "Writes schedulingStatus, currentStage — now validated by engine", severity: "info" },
    { file: "src/components/dashboards/WarehouseCommandCenter.tsx", type: "direct_status_write", description: "Validated via validateStatusWrite — warehouse module", severity: "info" },
    { file: "src/components/dashboards/WarehouseManagerDashboard.tsx", type: "direct_status_write", description: "Writes materialReadiness — mirrors WarehouseCommandCenter", severity: "info" },
    { file: "src/pages/InstallerDashboardPage.tsx", type: "direct_status_write", description: "Validated via validateStatusWrite — installer module", severity: "info" },
    { file: "src/pages/ProjectCloseout.tsx", type: "direct_status_write", description: "Validated via validateStatusWrite — qa_closeout module", severity: "info" },
    { file: "src/pages/QuoteDetail.tsx", type: "direct_status_write", description: "Creates jobs with initial currentStage", severity: "info" },
    { file: "src/pages/OrderingQueue.tsx", type: "direct_status_write", description: "Writes orderingStatus, materialReadiness — validated by engine", severity: "info" },
  ];
}

/**
 * Files that inline-derive entity truth instead of using shared selectors.
 * Selector architecture rule: if a selector exists, use it.
 */
export function getInlineDerivationViolations(): WorkflowViolation[] {
  return [
    // MIGRATED — now using shared selectors
    { file: "src/pages/AppointmentResults.tsx", type: "inline_derivation", description: "Migrated to getResultMetrics() from appointmentResultSelectors", severity: "info" },
    { file: "src/pages/CommandDashboard.tsx", type: "inline_derivation", description: "Migrated to getResultMetrics/getRevenueLeaks/getRepPerformanceSummary/getDailyTrends", severity: "info" },
    { file: "src/pages/QuotesList.tsx", type: "inline_derivation", description: "Migrated to getAllOpportunities/getOpportunityStats from opportunitySelectors", severity: "info" },
    { file: "src/pages/AppointmentsPage.tsx", type: "inline_derivation", description: "Migrated to appointmentSelectors (getAllAppointments)", severity: "info" },
    { file: "src/components/dashboards/SalesCommandCenter.tsx", type: "inline_derivation", description: "Migrated to appointmentSelectors (getCommandCenterAppointments)", severity: "info" },
    // REMAINING — inline derivation still present (non-critical, display-only contexts)
    { file: "src/pages/FollowUpsPage.tsx", type: "inline_derivation", description: "Has inline risk/overdue enrichment — followUpSelectors available", severity: "warning" },
    { file: "src/components/dashboards/SalesIntelligenceReport.tsx", type: "inline_derivation", description: "Inline close rate calculation — opportunitySelectors available", severity: "warning" },
    { file: "src/pages/IntelligenceDashboard.tsx", type: "inline_derivation", description: "Inline rep close rate — opportunitySelectors available", severity: "warning" },
    { file: "src/pages/Pipeline.tsx", type: "inline_derivation", description: "Inline lead scoring — leadSelectors available", severity: "warning" },
    { file: "src/pages/DispositionReport.tsx", type: "inline_derivation", description: "Uses calculateCloseRatePercent correctly — further selectors available", severity: "info" },
    { file: "src/pages/RepScorecards.tsx", type: "inline_derivation", description: "Uses commissionEngine correctly — no inline derivation", severity: "info" },
    { file: "src/pages/Commissions.tsx", type: "inline_derivation", description: "Uses commissionEngine correctly — no inline derivation", severity: "info" },
  ];
}

/**
 * Get a full audit summary with counts by severity
 */
export function getWorkflowAuditSummary() {
  const rawChecks = getRawStageCheckFiles();
  const directWriters = getDirectStatusWriters();
  const inlineDerivations = getInlineDerivationViolations();
  const all = [...rawChecks, ...directWriters, ...inlineDerivations];

  return {
    total: all.length,
    migrated: all.filter(v => v.severity === "info").length,
    warnings: all.filter(v => v.severity === "warning").length,
    critical: all.filter(v => v.severity === "critical").length,
    violations: all,
    migrationProgress: Math.round((all.filter(v => v.severity === "info").length / all.length) * 100),
  };
}

/**
 * Validate a status write attempt at runtime.
 * Returns violation details if the write is invalid, null if OK.
 */
export function validateStatusWrite(
  callerModule: string,
  field: StatusFieldKey,
  value: string,
): { valid: boolean; violation?: string } {
  const ownership = STATUS_OWNERSHIP[field];
  if (!ownership) return { valid: false, violation: `Unknown status field: ${field}` };

  // Check module ownership.
  // "workflow_engine" is a privileged cross-module coordinator — it may derive/write any field.
  // All other callers must own the field themselves.
  if (callerModule !== "workflow_engine" && ownership.ownerModule !== callerModule) {
    return {
      valid: false,
      violation: `Module "${callerModule}" cannot write "${field}" — owned by "${ownership.ownerModule}"`,
    };
  }

  // Check value validity
  if (!ownership.allowedValues.includes(value)) {
    return {
      valid: false,
      violation: `Invalid value "${value}" for "${field}" — allowed: ${ownership.allowedValues.join(", ")}`,
    };
  }

  return { valid: true };
}
